<script>
import _ from 'lodash';
import moment from 'moment';
import { mask } from 'vue-the-mask';
import vSelect from 'vue-select';
import { authComputed, authMethods, clientComputed, clientMethods, trainerComputed } from '@state/helpers';

export default {
  name: 'PersonalInformationMobile',
  components: {
    'v-select': vSelect,
  },
  directives: { mask },
  data() {
    return {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      phoneNumber: '',
      updatingClient: false,
      genderOptions: ['Male', 'Female', 'Not specified'],
    };
  },
  validations: {
    phoneNumber: {
      isPhone(value) {
        if (value) {
          return /^(?=(?:^(?:\+?1\s*(?:[.-]\s*)?)?(?!(?:(?:.*\(.*)|(?:.*\).*)))(?:[2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))|(?:.*\((?:[2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\).*))(?:\+?1\s*(?:[.-]\s*)?)?(?:\(?([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\)?)\s*(?:[.-]\s*)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d{1,15}))?$/.test(
            value
          ); // phone valid
        } else return true;
      },
    },
    dateOfBirth: {
      is13OrOlder(value) {
        const dateISO = moment(value).toISOString();
        const is13ISO = moment().toISOString();

        const date = moment(dateISO);
        const is13 = moment(is13ISO).subtract(13, 'years');

        if (value === null || value === '' || (date && value && date.isBefore(is13))) {
          return true;
        } else {
          return false;
        }
      },
    },
  },
  mounted() {
    this.initalizeLocalState();
  },
  methods: {
    ...authMethods,
    ...clientMethods,
    initalizeLocalState() {
      this.firstName = this.userProfile.firstName;
      this.lastName = this.userProfile.lastName;
      this.dateOfBirth = this.userProfile.dob;
      this.gender = this.userProfile.gender;
      this.phoneNumber = this.userProfile.phone;
    },
    setDob(event) {
      this.$v.dateOfBirth.$touch();
      if (event) {
        this.dateOfBirth = moment(event.target.value).toISOString();
      }
    },
    async handleSubmitPersonalInfo() {
      this.updatingClient = true;

      const payload = {
        client: {
          profile: {
            ...this.userProfile,
            firstName: this.firstName,
            lastName: this.lastName,
            dob: this.dateOfBirth,
            gender: this.gender,
            phone: this.phoneNumber,
          },
        },
      };
      const response = await this.updateClient({ clientId: this.clientId, trainerId: this.trainerId, payload });

      if (response.success === false) {
        console.error('response: ', response);
      } else {
        const updatedUser = _.cloneDeep(this.currentUser);
        updatedUser.user = response.client;
        this.setCurrentUser(updatedUser);

        await this.updateOnboardingProgress(1);
        this.$router.push({ name: 'personalGoals' }).catch((err) => {
          if (err !== undefined) {
            console.error(err);
          }
        });
      }

      this.updatingClient = false;
    },
  },
  computed: {
    ...authComputed,
    ...clientComputed,
    ...trainerComputed,
    dob() {
      if (this.dateOfBirth) {
        return moment.utc(this.dateOfBirth).format('MM/DD/YYYY');
      }
      return '';
    },
  },
};
</script>

<template>
  <div class="page">
    <BaseProgressBar class="base-progress-bar" :limit="7" :progress="1" />
    <div class="container">
      <div class="header">
        <div class="title">Help me complete your profile.</div>
      </div>
      <div class="form">
        <form>
          <div>
            <BaseInputTextWithLabel
              id="first-name"
              type="text"
              label="First Name"
              classStyle="simple"
              placeholder="First Name"
              v-model="firstName"
            />
            <BaseInputTextWithLabel
              id="last-name"
              type="text"
              label="Last Name"
              classStyle="simple"
              placeholder="Last Name"
              v-model="lastName"
            />
            <BaseInputTextWithLabel
              id="date-of-birth"
              type="text"
              label="Date Of Birth"
              classStyle="simple"
              v-mask="'##/##/####'"
              :value="dob"
              @change.native="setDob"
            />
            <label class="label-gender">Gender</label>
            <v-select
              id="gender"
              name="gender"
              placeholder="Choose gender"
              :options="genderOptions"
              :searchable="false"
              :clearable="false"
              v-model="gender"
            />
            <BaseInputTextWithLabel
              id="phone-number"
              type="phone"
              label="Phone Number"
              classStyle="simple"
              placeholder="(000) 000-0000"
              v-model="phoneNumber"
              v-mask="'(###) ###-####'"
            />
          </div>
        </form>
      </div>
    </div>
    <div id="next">
      <BaseButton class="large" @click="handleSubmitPersonalInfo" :disabled="updatingClient">
        next
        <img src="@assets/images/icons/right-arrow.svg" />
      </BaseButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-id-pattern
// stylelint-disable selector-pseudo-element-no-unknown

.page {
  margin: auto;
  position: relative;
  width: 400px;

  .progress-bar {
    left: 40px;
    position: absolute;
    right: 40px;
    top: 40px;
  }

  .container {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 18px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    height: 65vh;
    margin-top: 56px;
    overflow: auto;
    padding-bottom: 10rem;
    position: relative;
    width: 400px;

    .header {
      height: 240px;
      margin: 0;
      text-align: start;
      width: 100%;

      .title {
        color: black;
        font-family: $base-font-family;
        font-size: 36px;
        font-style: normal;
        font-weight: 800;
        line-height: 42px;
        margin: 90px auto 0;
        width: 90%;
      }
    }

    .form {
      margin-top: 24px;
      form {
        flex-direction: column;
        justify-content: space-between;
        position: relative;
        width: 100%;
        ::v-deep label {
          font-family: $proxima-nova;
          font-weight: 600;
        }
        ::v-deep input {
          font-family: $proxima-nova;
          font-weight: normal;
          &::placeholder {
            font-family: $proxima-nova;
            font-weight: normal;
          }
        }
        #first-name {
          margin-bottom: 10px;
          margin-left: 5%;
          width: 90%;
        }

        #last-name {
          margin-bottom: 10px;
          margin-left: 5%;
          width: 90%;
        }

        #date-of-birth {
          float: left;
          margin-bottom: 10px;
          margin-left: 5%;
          width: 43%;
        }

        #gender {
          float: right;
          margin-bottom: 10px;
          margin-right: 5%;
          width: 43%;
        }

        #phone-number {
          margin-bottom: 70px;
          margin-left: 5%;
          width: 90%;
        }

        .label-gender {
          align-self: start;
          color: #000;
          font-family: $proxima-nova;
          font-size: 12px;
          margin-bottom: 2.375px;
          margin-left: 18.5px;
        }
      }
    }
  }
  #next {
    background-color: white;
    bottom: 0;
    float: right;
    margin-bottom: 20px;
    position: fixed;
    position: absolute;
    right: 0;
    width: 100%;
    button {
      float: right;
      margin-bottom: 20px;
      margin-left: auto;
      margin-right: 5%;
      width: 40%;
    }

    button:disabled {
      background-color: lighten($color: #4059e1, $amount: 20);
    }
  }
}
::v-deep {
  .v-select > .vs__dropdown-toggle {
    background-color: #f3f3f3;
    border-radius: 0.8rem;
    display: flex;
    flex-wrap: nowrap;
    margin-bottom: 0;
    .vs__selected-options {
      color: #000;
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      font-size: 16px;
      font-style: normal;
      font-weight: normal;
      line-height: 23px;
      mix-blend-mode: normal;
      padding: 7px 2px;
      transition: 0.3s all ease-in-out;
      span {
        color: #000;
        font-family: $proxima-nova;
        padding: 0;
        white-space: nowrap;
      }
    }
  }
  .v-select > .vs__dropdown-menu {
    background-color: #f3f3f3;
  }
}
</style>
